import React, { useCallback, useMemo } from 'react';
import {
  CELSIUS_ID,
  SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import { useHistory } from 'react-router-dom';
import AlertRow from './AlertRow';
import {
  ALERT_LIST_COLUMNS,
  COLUMN_ORDER,
  buildColumnVisibility,
} from './alertListColumns';
import { getPublicDashboardToken } from '../../services/dashboardApi';
import HasRolePrivileges from '../../../highorder/HasRolePrivileges';
import { RaaRoles } from '../../../../utility/SecurityRules';

// A single alert record, shaped exactly like the real /alerts endpoint
// response so AlertRow can render it without any special-casing.
const makeStubAlert = ({
  id,
  description,
  componentName,
  channelKey,
  startThreshold,
  lastSampleValue,
  triggered,
  triggeredTimestamp,
  lastSampleTimestamp,
}) => ({
  id,
  triggeredAlertEventId: triggered ? id * 10 : null,
  channelId: id,
  triggeredTimestamp: triggered ? triggeredTimestamp : null,
  description,
  temperatureScale: null,
  timezone: 'America/New_York',
  isSuspended: false,
  isDeviceSuspended: false,
  dateCreated: 1782325171000,
  dateEnded: null,
  startThreshold,
  startOperatorTypeId: 1, // ">"
  conditionType: 'channel_value',
  acknowledgementTimestamp: null,
  channelType: 'TEMPERATURE',
  channelGroupName: componentName,
  deviceName: 'Example Device',
  deviceType: 'RAMAX',
  componentName,
  lastSampleTimestamp,
  lastSampleValue,
  isFavorite: false,
  actionCount: 1,
  channelKey: {
    macAddress: '50:62:55:70:A7:8C',
    channelGroupMacAddress: '08:B9:5F:B6:9E:E8',
    channelGroupType: 16,
    channelGroupIndex: 0,
    channelType: 1,
    channelIndex: 0,
    subscriptionId: 1,
    channelKey,
    channelGroupKey: '50:62:55:70:A7:8C_08:B9:5F:B6:9E:E8_16_0',
    sensorChannelGroupKey: '50:62:55:70:A7:8C_08:B9:5F:B6:9E:E8',
  },
  isPushing: false,
  customUnit: null,
  tagIds: [],
  tagNames: ['Production'],
});

// Six illustrative rows for the empty state. Timestamps are pinned to a fixed
// date so the widget renders identically across reloads.
const STUB_ALERTS = [
  makeStubAlert({
    id: 1001,
    description: 'Assembly Line Heat Index Alert',
    componentName: 'Assembly Line',
    channelKey: 'stub_1001',
    startThreshold: 91,
    lastSampleValue: '94.11',
    triggered: true,
    triggeredTimestamp: 1770320825000,
    lastSampleTimestamp: 1770320825000,
  }),
  makeStubAlert({
    id: 1002,
    description: 'Breakroom Heat Index Alert',
    componentName: 'Breakroom',
    channelKey: 'stub_1002',
    startThreshold: 91,
    lastSampleValue: '88.30',
    triggered: false,
    triggeredTimestamp: null,
    lastSampleTimestamp: 1770320465000,
  }),
  makeStubAlert({
    id: 1003,
    description: 'Loading Bay Heat Index Alert',
    componentName: 'Loading Bay',
    channelKey: 'stub_1003',
    startThreshold: 91,
    lastSampleValue: '90.05',
    triggered: false,
    triggeredTimestamp: null,
    lastSampleTimestamp: 1770320045000,
  }),
  makeStubAlert({
    id: 1004,
    description: 'Packaging Heat Index Alert',
    componentName: 'Packaging',
    channelKey: 'stub_1004',
    startThreshold: 91,
    lastSampleValue: '89.62',
    triggered: false,
    triggeredTimestamp: null,
    lastSampleTimestamp: 1770319925000,
  }),
  makeStubAlert({
    id: 1005,
    description: 'Receiving Heat Index Alert',
    componentName: 'Receiving',
    channelKey: 'stub_1005',
    startThreshold: 91,
    lastSampleValue: '87.44',
    triggered: false,
    triggeredTimestamp: null,
    lastSampleTimestamp: 1770319745000,
  }),
  makeStubAlert({
    id: 1006,
    description: 'Zone Aisle Heat Index Alert',
    componentName: 'Zone Aisle',
    channelKey: 'stub_1006',
    startThreshold: 91,
    lastSampleValue: '70.95',
    triggered: false,
    triggeredTimestamp: null,
    lastSampleTimestamp: 1770320565000,
  }),
];

// Build 24h of mocked trend samples for a given "current" value. Wanders
// gently around the value and drifts up toward the threshold at the end for
// triggered rows so the sparkline tells a plausible story.
const buildStubSamples = (currentValue, threshold, isTriggered) => {
  const now = Date.now();
  const points = 24;
  const hourMs = 60 * 60 * 1000;
  const base = Number(currentValue);
  const samples = [];
  for (let i = points - 1; i >= 0; i -= 1) {
    const t = new Date(now - i * hourMs);
    // Deterministic pseudo-random wobble so the chart is stable across renders.
    const wobble = Math.sin((points - i) * 1.3) * 1.5;
    let value = base + wobble - 2;
    if (isTriggered && i < 4) {
      // Ramp up above the threshold near the end.
      value = threshold + (4 - i) * 0.8;
    }
    samples.push({ time: t, value });
  }
  return samples;
};

const STUB_SAMPLES_BY_CHANNEL_KEY = STUB_ALERTS.reduce((acc, alert) => {
  acc[alert.channelKey.channelKey] = buildStubSamples(
    alert.lastSampleValue,
    alert.startThreshold,
    alert.triggeredTimestamp !== null,
  );
  return acc;
}, {});

const AlertsListWidgetDefault = () => {
  const history = useHistory();
  const isPublicView = !!getPublicDashboardToken();
  const userPreferences = {
    temperatureScale: CELSIUS_ID,
    airSpeedScale: SPEED_METERS,
    timezone: 'America/New_York',
  };

  const columnVisibility = useMemo(() => buildColumnVisibility(null), []);
  const isVisible = useCallback(
    (columnId) => columnVisibility[columnId],
    [columnVisibility],
  );

  const handleCreateAlert = () => {
    history.push('/s/alerts/new');
  };

  return (
    <div className="alerts-list-default position-relative h-100 w-100">
      {/* Ghosted preview table — fills the widget as the backdrop. */}
      <div
        aria-hidden="true"
        className="h-100 w-100"
        style={{
          opacity: 0.35,
          filter: 'grayscale(35%)',
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <div className="px-3 pt-2 text-muted small font-weight-bold">
          Heat Index Alerts
        </div>
        <table className="table table-sm table-striped mb-0">
          <thead>
            <tr>
              {COLUMN_ORDER.filter(isVisible).map((id) => (
                <th key={id} className="text-muted small">
                  {ALERT_LIST_COLUMNS[id].label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {STUB_ALERTS.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                isVisible={isVisible}
                trendSamples={STUB_SAMPLES_BY_CHANNEL_KEY[alert.channelKey.channelKey]}
                trendsLoading={false}
                userPreferences={userPreferences}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Floating call-to-action card layered over the ghosted table. */}
      <div
        className="position-absolute d-flex align-items-center justify-content-center"
        style={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          padding: '1rem',
        }}
      >
        <div
          className="shadow-sm rounded d-flex align-items-center"
          style={{
            background: 'rgba(255, 255, 255, 0.96)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            padding: '1rem 1.25rem',
            gap: '1rem',
            maxWidth: '66%',
            width: '100%',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="text-primary font-weight-bold mb-1">
              No alerts set yet — stay ahead of trouble by adding one now!
            </div>
            <div className="text-muted small">
              Alerts notify you when temperature, humidity or other conditions
              exceed defined thresholds.
            </div>
          </div>
          {!isPublicView && (
            <HasRolePrivileges targetRoleList={[RaaRoles.MANAGER, RaaRoles.ADMIN]}>
              <button
                type="button"
                className="btn btn-primary flex-shrink-0"
                onClick={handleCreateAlert}
              >
                Create Alert
              </button>
            </HasRolePrivileges>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertsListWidgetDefault;
